import React, { useMemo, useState, useEffect } from "react";
import { Input } from "./ui/input";

/**
 * Fase 11.5 Sprint 1 — Item 1: Calculadora de ROI.
 *
 * Compara el costo del Diagnóstico (COP 350.000) con el costo típico de
 * contratar un consultor tradicional para hacer el mismo análisis precontractual.
 *
 * Fuentes de los rangos (defensibles legalmente):
 * - Servicios profesionales en SECOP II para "asesoría/consultoría jurídica
 *   contractual" típicamente entre COP 1.500.000 y 6.000.000 por proceso,
 *   dependiendo de complejidad y modalidad.
 * - Tiempo típico: 5–15 días hábiles.
 *
 * Comparación honesta:
 * - NO afirma que sustituye al consultor. SÍ afirma que entrega un INSUMO
 *   estructurado equivalente al trabajo inicial del consultor (en horas).
 * - Disclaimer en pie del componente.
 *
 * Props:
 *   valorContrato: número (COP) — viene del intake (`valor_estimado_cop`).
 *   plazoMeses: número (opcional).
 *   modalidad: string (opcional) — para escalar el rango.
 */

const PRECIO_DIAGNOSTICO = 350000;
const PLAZO_DIAGNOSTICO_HABILES_DESDE = 1;
const PLAZO_DIAGNOSTICO_HABILES_HASTA = 2;

// Rangos por modalidad (estimados defensibles)
const RANGOS_CONSULTOR_COP = {
  licitacion_publica: { min: 3000000, max: 6000000, dias: [10, 15] },
  seleccion_abreviada: { min: 2000000, max: 4500000, dias: [7, 12] },
  concurso_meritos: { min: 2500000, max: 5000000, dias: [8, 14] },
  contratacion_directa: { min: 1500000, max: 3000000, dias: [5, 10] },
  minima_cuantia: { min: 1500000, max: 2500000, dias: [5, 8] },
  default: { min: 1500000, max: 4000000, dias: [7, 12] },
};

function formatCOP(v) {
  if (v == null) return "—";
  return "$ " + Math.round(v).toLocaleString("es-CO");
}

function pctSavings(precioDiag, rangoMin) {
  if (!rangoMin || rangoMin <= 0) return 0;
  return Math.round(((rangoMin - precioDiag) / rangoMin) * 100);
}

export default function ROICalculator({ valorContrato = 0, plazoMeses = 0, modalidad = "" }) {
  const [editValor, setEditValor] = useState(valorContrato || 0);
  const [editModalidad, setEditModalidad] = useState(modalidad || "default");

  useEffect(() => {
    if (valorContrato) setEditValor(valorContrato);
  }, [valorContrato]);
  useEffect(() => {
    if (modalidad) setEditModalidad(modalidad in RANGOS_CONSULTOR_COP ? modalidad : "default");
  }, [modalidad]);

  // Escala el rango por valor del contrato (heurística simple).
  // Si valor > 500M → premium (+20%). Si valor > 1.000M → +40%.
  const rango = useMemo(() => {
    const base = RANGOS_CONSULTOR_COP[editModalidad] || RANGOS_CONSULTOR_COP.default;
    let mult = 1;
    if (editValor >= 1_000_000_000) mult = 1.4;
    else if (editValor >= 500_000_000) mult = 1.2;
    return {
      min: Math.round(base.min * mult),
      max: Math.round(base.max * mult),
      dias: base.dias,
    };
  }, [editValor, editModalidad]);

  const ahorroMin = rango.min - PRECIO_DIAGNOSTICO;
  const ahorroMax = rango.max - PRECIO_DIAGNOSTICO;
  const pctMin = pctSavings(PRECIO_DIAGNOSTICO, rango.min);
  const pctMax = pctSavings(PRECIO_DIAGNOSTICO, rango.max);
  const diasMin = rango.dias[0] - PLAZO_DIAGNOSTICO_HABILES_HASTA;
  const diasMax = rango.dias[1] - PLAZO_DIAGNOSTICO_HABILES_HASTA;

  return (
    <div
      className="border border-emerald-300 bg-emerald-50/40 p-6 mb-6 rounded-sm"
      data-testid="roi-calculator"
    >
      <p className="text-[11px] uppercase tracking-widest text-emerald-800 font-medium mb-1">
        ¿Vale la pena el Diagnóstico?
      </p>
      <h3 className="font-serif text-2xl text-gray-900 mb-2">Compáralo con un consultor tradicional</h3>
      <p className="text-sm text-gray-700 mb-5">
        Los rangos provienen de procesos típicos publicados en SECOP II para
        "asesoría jurídica contractual". Tu costo real puede variar.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-gray-300 p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Consultor tradicional</p>
          <p className="font-serif text-xl text-gray-900 mt-1" data-testid="roi-range-consultor">
            {formatCOP(rango.min)} – {formatCOP(rango.max)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Entrega típica: {rango.dias[0]}–{rango.dias[1]} días hábiles
          </p>
        </div>
        <div className="bg-emerald-100 border border-emerald-400 p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-widest text-emerald-800">Diagnóstico Procure</p>
          <p className="font-serif text-xl text-gray-900 mt-1">{formatCOP(PRECIO_DIAGNOSTICO)}</p>
          <p className="text-[11px] text-emerald-800 mt-1">
            Entrega: {PLAZO_DIAGNOSTICO_HABILES_DESDE}–{PLAZO_DIAGNOSTICO_HABILES_HASTA} días hábiles
          </p>
        </div>
        <div className="bg-white border border-emerald-400 p-4 rounded-sm">
          <p className="text-[10px] uppercase tracking-widest text-emerald-800">Tu ahorro estimado</p>
          <p className="font-serif text-xl text-emerald-900 mt-1" data-testid="roi-savings">
            {formatCOP(ahorroMin)} – {formatCOP(ahorroMax)}
          </p>
          <p className="text-[11px] text-emerald-800 mt-1">
            Aproximadamente {pctMin}% – {pctMax}% menos · {diasMin}–{diasMax} días más rápido
          </p>
        </div>
      </div>

      {/* Inputs editables para que el user explore */}
      <details className="text-xs text-gray-600">
        <summary className="cursor-pointer underline">Ajustar parámetros</summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              Valor estimado del contrato (COP)
            </label>
            <Input
              type="number"
              value={editValor}
              onChange={(e) => setEditValor(Number(e.target.value) || 0)}
              className="rounded-sm h-9"
              data-testid="roi-input-valor"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              Modalidad
            </label>
            <select
              value={editModalidad}
              onChange={(e) => setEditModalidad(e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white text-sm h-9"
              data-testid="roi-input-modalidad"
            >
              <option value="default">No estoy seguro</option>
              <option value="licitacion_publica">Licitación pública</option>
              <option value="seleccion_abreviada">Selección abreviada</option>
              <option value="concurso_meritos">Concurso de méritos</option>
              <option value="contratacion_directa">Contratación directa</option>
              <option value="minima_cuantia">Mínima cuantía</option>
            </select>
          </div>
        </div>
      </details>

      <p className="text-[10px] text-gray-500 mt-4 leading-snug">
        <strong>Aviso.</strong> Los rangos son estimaciones basadas en contratos publicados en SECOP II
        y promedios del mercado. El costo real de un consultor depende de complejidad, urgencia
        y experiencia. El Diagnóstico de Procure es un <strong>insumo de apoyo</strong> y
        no sustituye una asesoría legal integral cuando esta sea necesaria.
      </p>
    </div>
  );
}
